<template>
  <b-card
    no-body
  >
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label>Mostrar</label>
          <v-select
            v-model="perPage"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <b-button
            v-if="userData().role === 'admin'"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            @click="productAddSidebarActive = true"
          >
            <span class="align-middle">Nuevo</span>
          </b-button>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              placeholder="Buscar..."
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <div class="mx-2 mb-2">
      <b-row>
        <!-- Pagination info -->
        <b-col
          v-if="productsMeta"
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">Mostrando del {{ productsMeta.from }} al {{ productsMeta.to }} de {{ productsMeta.total }} registros</span>

          <download-excel
            v-if="productsData"
            class="btn"
            :data="productsData"
            :fields="excelFields"
            worksheet="Productos"
            :name="'productos_' + getCurrentDate() + '.xls'"
          >
            <feather-icon icon="DownloadIcon" />
            Descargar Excel
          </download-excel>
        </b-col>
      </b-row>
    </div>

    <b-table
      class="position-relative"
      hover
      responsive
      small
      :busy="tableIsBusy"
      primary-key="id"
      show-empty
      empty-text="No se encontraron registros coincidientes"
      :items="productsData"
      :fields="productsFields"
      :per-page="perPage"
    >
      <template #table-busy>
        <div class="text-center text-dark my-2">
          <b-spinner class="align-middle" />
        </div>
      </template>

      <template #head(id)="data">
        <span
          class="text-dark pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}
        </span>
      </template>

      <template #head(code)="data">
        <span
          class="text-dark pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}
        </span>
      </template>

      <template #head(sku)="data">
        <span
          class="text-dark pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}
        </span>
      </template>

      <template #head(description)="data">
        <span
          class="text-secondary pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}
        </span>
      </template>

      <template #head(inventory)="data">
        <span class="text-secondary">{{ data.label }}</span>
      </template>

      <!-- Column: Id -->
      <template #cell(id)="data">
        <b-link
          class="font-weight-bold text-primary"
          @click="$emit('addProductTab', data.item.id)"
        >
          {{ data.item.id }}
        </b-link>
      </template>

      <!-- Column: Code -->
      <template #cell(code)="data">
        <b-link
          class="font-weight-bold text-primary"
          @click="$emit('addProductTab', data.item.id)"
        >
          {{ data.item.code }}
        </b-link>
      </template>

      <!-- Column: SKU -->
      <template #cell(sku)="data">
        <span class="text-body">{{ data.item.sku || '-' }}</span>
      </template>

      <!-- Column: Description -->
      <template #cell(description)="data">
        <span class="text-body">{{ data.item.description }}</span>
      </template>

      <!-- Column: Inventory -->
      <template #cell(inventory)="data">
        <div
          v-if="data.item.product_variants && data.item.product_variants.length"
          class="d-flex align-items-center"
        >
          <b-badge
            v-b-tooltip.hover
            :variant="getInventoryVariant(getProductInventoryTotals(data.item).available_boxes)"
            :title="`Unidades — Disp.: ${getProductInventoryTotals(data.item).available_boxes}  | Reserv.: ${getProductInventoryTotals(data.item).reserved_boxes}  | Vend.: ${getProductInventoryTotals(data.item).sold_boxes}`"
            class="mr-50"
          >
            {{ getProductInventoryTotals(data.item).available_boxes }} uds.
          </b-badge>
        </div>
        <span
          v-else
          class="text-muted"
        >-</span>
      </template>

      <!-- Column: Variants Count -->
      <template #cell(product_variants_count)="data">
        <b-badge
          v-if="data.item.product_variants_count"
          variant="light-primary"
        >
          {{ data.item.product_variants_count }} variantes
        </b-badge>
        <span
          v-else
          class="text-muted"
        >
          Sin variantes
        </span>
      </template>

      <!-- Column: Base Price -->
      <template #cell(base_price)="data">
        <span class="text-body">
          {{ data.item.base_price ? `Q${parseFloat(data.item.base_price).toFixed(2)}` : '-' }}
        </span>
      </template>

      <!-- Column: Width -->
      <template #cell(width)="data">
        <span class="text-body">{{ data.item.width || '-' }}</span>
      </template>

      <!-- Column: Web Name -->
      <template #cell(web_name)="data">
        <span class="text-body">{{ data.item.web_name || '-' }}</span>
      </template>

      <!-- Column: Color -->
      <template #cell(color)="data">
        <div class="d-flex align-items-center">
          <div
            v-if="data.item.color_data && data.item.color_data.hex_code"
            class="color-preview mr-1"
            :style="{ backgroundColor: data.item.color_data.hex_code }"
          />
          <span>{{ data.item.color || '-' }}</span>
        </div>
      </template>

      <!-- Column: Category -->
      <template #cell(category)="data">
        <b-badge
          v-if="data.item.category"
          variant="light-info"
        >
          {{ data.item.category }}
        </b-badge>
        <span
          v-else
          class="text-muted"
        >
          -
        </span>
      </template>

      <!-- Column: Status -->
      <template #cell(is_active)="data">
        <b-badge :variant="`light-${ data.item.is_active === 'Y' ? 'success' : 'danger' }`">
          {{ data.item.is_active === 'Y' ? 'Activo' : 'Inactivo' }}
        </b-badge>
      </template>

      <!-- Column: Default Image -->
      <template #cell(default_image)="data">
        <div v-if="data.item.default_image">
          <img
            :src="getImageUrl(data.item.default_image.image_path)"
            :alt="data.item.default_image.alt_text || 'Imagen del producto'"
            class="product-thumbnail"
            @error="handleImageError"
          >
        </div>
        <span
          v-else
          class="text-muted"
        >
          Sin imagen
        </span>
      </template>
    </b-table>

    <div class="mx-2 mb-2">
      <b-row>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="12"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-if="productsMeta"
            v-model="currentPage"
            :total-rows="productsMeta.total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <!-- Add Product Sidebar -->
    <products-add
      :product-add-sidebar-active.sync="productAddSidebarActive"
      @refreshData="getProducts"
    />
  </b-card>
</template>

<script>
import axios from '@axios'
import { buildStorageUrl, NO_IMAGE_URL } from '@/utils/urls'
import vSelect from 'vue-select'
import { required } from '@/utils/validations/validations'
import downloadExcel from 'vue-json-excel'
import Ripple from 'vue-ripple-directive'
import ProductsAdd from './ProductsAdd.vue'

export default {
  components: {
    vSelect,
    downloadExcel,
    ProductsAdd,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      required,
      productAddSidebarActive: false,
      searchQuery: '',
      searchQueryTimeout: null,
      sortField: 'id',
      sortDesc: 'desc',
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100, 'Todos'],
      currentPage: 1,
      productsFields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'code', label: 'Código', sortable: true },
        { key: 'sku', label: 'SKU', sortable: true },
        { key: 'description', label: 'Descripción', sortable: true },
        { key: 'inventory', label: 'Inventario', sortable: false },
        { key: 'product_variants_count', label: 'Variantes', sortable: false },
        { key: 'base_price', label: 'Precio Base', sortable: true },
        { key: 'width', label: 'Ancho', sortable: true },
        { key: 'web_name', label: 'Nombre Web', sortable: true },
        { key: 'color', label: 'Color', sortable: false },
        { key: 'category', label: 'Categoría', sortable: false },
        { key: 'is_active', label: 'Estado', sortable: true },
        { key: 'default_image', label: 'Imagen', sortable: false },
        { key: 'created_at', label: 'Creado', sortable: true },
        { key: 'updated_at', label: 'Actualizado', sortable: true },
      ],
      productsData: null,
      productsMeta: null,
      tableIsBusy: true,
    }
  },

  computed: {
    userData() {
      return () => JSON.parse(localStorage.getItem('DuttonAuthUser'))
    },
    excelFields() {
      return {
        Código: 'code',
        SKU: 'sku',
        Descripción: 'description',
        'Nombre Web': 'web_name',
        Color: 'color',
        Categoría: 'category',
        'Precio Base': 'base_price',
        Tallas: 'product_variants_count',
        Disponible: { callback: row => this.getProductInventoryTotals(row).available_boxes },
        Reservado: { callback: row => this.getProductInventoryTotals(row).reserved_boxes },
        Vendido: { callback: row => this.getProductInventoryTotals(row).sold_boxes },
        Estado: { field: 'is_active', callback: value => (value === 'Y' ? 'Activo' : 'Inactivo') },
        Creado: 'created_at',
      }
    },
  },

  watch: {
    perPage() {
      this.tableIsBusy = true
      this.getProducts()
    },
    currentPage() {
      this.tableIsBusy = true
      this.getProducts()
    },
    searchQuery() {
      clearTimeout(this.searchQueryTimeout)
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        this.tableIsBusy = true
        this.getProducts()
      } else {
        this.searchQueryTimeout = setTimeout(() => {
          this.tableIsBusy = true
          this.getProducts()
        }, 300)
      }
    },
  },

  created() {
    this.getProducts()
  },

  methods: {
    getProductInventoryTotals(product) {
      const totals = { available_boxes: 0, reserved_boxes: 0, sold_boxes: 0 }
      if (!product || !product.product_variants) return totals
      product.product_variants.forEach(variant => {
        const s = variant.stock || { available_boxes: 0, reserved_boxes: 0, sold_boxes: 0 }
        totals.available_boxes += Number(s.available_boxes || 0)
        totals.reserved_boxes += Number(s.reserved_boxes || 0)
        totals.sold_boxes += Number(s.sold_boxes || 0)
      })
      return totals
    },
    getInventoryVariant(value) {
      if (value > 5) return 'light-success'
      if (value > 0) return 'light-warning'
      return 'light-danger'
    },
    getProducts() {
      if (this.perPage === 'Todos') {
        this.perPage = this.productsMeta ? this.productsMeta.total : 1000
      }

      axios
        .get(`product?perPage=${this.perPage}&page=${this.currentPage}&query=${this.searchQuery}&sortField=${this.sortField}&sortDesc=${this.sortDesc}`)
        .then(response => {
          this.tableIsBusy = false
          this.productsMeta = response.data.meta
          this.productsData = response.data.data
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    sortColumn(field) {
      if (this.sortField === field) {
        if (this.sortDesc === 'asc') {
          this.sortDesc = 'desc'
        } else {
          this.sortDesc = 'asc'
        }
      } else {
        this.sortField = field
        this.sortDesc = 'asc'
      }
      this.getProducts()
    },

    getImageUrl(path) {
      if (!path) return NO_IMAGE_URL
      return buildStorageUrl(path)
    },

    handleImageError(event) {
      const { target } = event
      target.src = NO_IMAGE_URL
    },

    getCurrentDate() {
      return new Date().toISOString().slice(0, 10)
    },

    showErrors(error) {
      if (error.response && error.response.data && error.response.data.message) {
        this.$bvToast.toast(error.response.data.message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } else {
        this.$bvToast.toast('Ha ocurrido un error inesperado', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.pointer {
  cursor: pointer;
}

.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #ddd;
}

.product-thumbnail {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
